import { useState } from 'react';
import { HelmetAsync } from '../components/HelmetAsync';
import { useReveal } from '../hooks/useReveal';
import { SectionLabel } from '../components/ui';
import { ProductCard, ProductRow } from '../components/ProductCard';
import { getFamilies } from '../data/products';
import { color, font, maxWidth } from '../styles/tokens';

// `defaultLayout` mirrors the prototype's productLayout prop ('Cards' | 'List').
export const ProductsPage = ({ defaultLayout = 'Cards' }) => {
  const families = getFamilies();
  const [layout, setLayout] = useState(defaultLayout === 'List' ? 'list' : 'cards');
  const isCards = layout !== 'list';

  // Re-scan reveal targets when the layout switches (new nodes mount).
  useReveal([layout]);

  const segOn = { background: color.accent, color: color.bg, border: 'none', borderRadius: 999, padding: '8px 18px', font: `600 13px ${font.display}`, cursor: 'pointer' };
  const segOff = { background: 'none', color: color.textMuted, border: 'none', borderRadius: 999, padding: '8px 18px', font: `600 13px ${font.display}`, cursor: 'pointer' };

  return (
    <div style={{ animation: 'vt-fade .4s ease' }}>
      <HelmetAsync
        title="Products — V-Tech Industries"
        description="Honing stones, honing tools, industrial brushes, dies & molds and receiving gauges — precision tools built to spec."
        keywords="honing stones, industrial brushes, dies, molds, receiving gauge, precision tools"
        url="/products"
      />

      <section style={{ maxWidth, margin: '0 auto', padding: '80px 28px 36px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
          <div data-reveal>
            <SectionLabel>[ OUR PRODUCTS ]</SectionLabel>
            <h1 style={{ margin: '18px 0 0', font: `700 clamp(38px,6vw,68px)/0.98 ${font.display}`, letterSpacing: '-.03em', color: color.text }}>
              Precision tools,<br />
              <span style={{ color: color.accent }}>built to spec</span>
            </h1>
          </div>
          <div data-reveal style={{ display: 'flex', gap: 6, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 999, padding: 5 }}>
            <button onClick={() => setLayout('cards')} style={isCards ? segOn : segOff}>Cards</button>
            <button onClick={() => setLayout('list')} style={!isCards ? segOn : segOff}>List</button>
          </div>
        </div>
      </section>

      <section style={{ maxWidth, margin: '0 auto', padding: '0 28px 80px', display: 'flex', flexDirection: 'column', gap: 56 }}>
        {families.map((f) => (
          <div key={f.name}>
            <div data-reveal style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 24, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,.1)' }}>
              <span style={{ font: `500 12px ${font.mono}`, color: color.accent }}>{f.idx}</span>
              <h2 style={{ margin: 0, font: `600 24px ${font.display}`, color: color.text }}>{f.name}</h2>
              <span style={{ font: `400 13px ${font.body}`, color: color.textFaint }}>{f.count} products</span>
            </div>

            {isCards ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 18 }}>
                {f.items.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid rgba(255,255,255,.1)', borderRadius: 16, overflow: 'hidden' }}>
                {f.items.map((p) => (
                  <ProductRow key={p.id} product={p} />
                ))}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
};
